import { Fragment, Suspense } from "react";
import { LatLngLiteral } from "leaflet";
import { CircularProgress, styled, Typography } from "@mui/material";
import { ErrorBoundary, FallbackProps } from "react-error-boundary";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { LayerProperties, IndicatorSelection, Indicator } from "../../../types";
import { useMapPositionBounds } from "../../../hooks/mapHooks";
import { useIndicator } from "../../../hooks/indicatorHooks";
import { useSelectedLayer } from "../../../hooks/selectionHooks";

import { LayerPropertiesDefaultPrepare } from "./LayerPropertiesDefaultPrepare";
import { LayerPropertiesFlandersPrepare } from "./LayerPropertiesFlandersPrepare";
import { PointAccordion } from "./PointAccordion";
import { sortBy } from "lodash-es";

interface Props {
  config: string;
  selection: IndicatorSelection;
  layerProperties: LayerProperties[];
  point: LatLngLiteral;
}

export const LayerPropertiesForMarker = (props: Props) => {
  const { config, selection, point, layerProperties } = props;
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const { data: indicator } = useIndicator(
    config,
    selection.indicatorId,
    layerProperties
  );
  const { data: layer } = useSelectedLayer(config, indicator, layerProperties);
  const bounds = useMapPositionBounds(point || { lat: 0, lng: 0 });
  const allLayers = params.get("pl") === "all";

  return render();
  function render() {
    if (!indicator) return null;
    if (!indicatorHasAttributes(indicator)) return null;

    return (
      <>
        {
          <ErrorBoundary
            fallbackRender={(props: FallbackProps) => (
              <PointAccordion
                defaultExpanded
                title={indicator.title}
                key={props.error.name}
              >
                <Typography color="error">
                  {t("label.unableToLoadProperties")}
                </Typography>
              </PointAccordion>
            )}
          >
            <Suspense
              fallback={
                <PointAccordion
                  defaultExpanded
                  title={
                    <>
                      {indicator.title}
                      <CircularProgress size={12} />
                    </>
                  }
                >
                  <></>
                </PointAccordion>
              }
            >
              <Styles title={indicator.title} defaultExpanded>
                {!allLayers &&
                  layer &&
                  layer.sourceType !== "INFORMATIE_VLAANDEREN" &&
                  indicator && (
                    <LayerPropertiesDefaultPrepare
                      layer={layer}
                      groupingKey={indicator.groupingKey}
                      groupingCount={indicator.layers.length}
                      attributes={layer.attributes}
                      mapPosition={{
                        ...bounds,
                        position: point || { lat: 0, lng: 0 },
                      }}
                    />
                  )}
                {!allLayers &&
                  layer &&
                  layer.sourceType === "INFORMATIE_VLAANDEREN" &&
                  indicator && (
                    <LayerPropertiesFlandersPrepare
                      layer={layer}
                      groupingKey={indicator.groupingKey}
                      groupingCount={indicator.layers.length}
                      attributes={layer.attributes}
                      mapPosition={{
                        ...bounds,
                        position: point || { lat: 0, lng: 0 },
                      }}
                    />
                  )}
                {allLayers &&
                  indicator &&
                  sortBy(indicator.layers, [
                    function (o) {
                      return o.properties
                        .map((property) => property.value.name)
                        .join(" | ");
                    },
                  ]).map((layer) => (
                    <Fragment key={`point-info-layer-${layer.id}`}>
                      <Typography variant="caption">
                        {layer.properties
                          .map((property) => property.value.name)
                          .join(" | ")}
                      </Typography>
                      {layer &&
                        layer.sourceType !== "INFORMATIE_VLAANDEREN" &&
                        indicator && (
                          <LayerPropertiesDefaultPrepare
                            layer={layer}
                            groupingKey={indicator.groupingKey}
                            groupingCount={indicator.layers.length}
                            attributes={layer.attributes}
                            mapPosition={{
                              ...bounds,
                              position: point || { lat: 0, lng: 0 },
                            }}
                          />
                        )}
                      {allLayers &&
                        layer &&
                        layer.sourceType === "INFORMATIE_VLAANDEREN" &&
                        indicator && (
                          <LayerPropertiesFlandersPrepare
                            layer={layer}
                            groupingKey={indicator.groupingKey}
                            groupingCount={indicator.layers.length}
                            attributes={layer.attributes}
                            mapPosition={{
                              ...bounds,
                              position: point || { lat: 0, lng: 0 },
                            }}
                          />
                        )}
                    </Fragment>
                  ))}
              </Styles>
            </Suspense>
          </ErrorBoundary>
        }
      </>
    );
  }
};

const Styles = styled(PointAccordion)`
  .MuiAccordionDetails-root {
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(3)};
  }

  .MuiTypography-caption {
    margin: ${({ theme }) => theme.spacing(2, 0, 1, 0)};
    display: block;
    border-bottom: 1px solid #ffffff33;
  }

  .layer-property {
    &:not(:last-child):after {
      display: block;
      content: "";
      position: absolute;
      bottom: -${({ theme }) => theme.spacing(3 / 2)};
      width: 100%;
      height: 1px;
      background-color: #ffffff39;
    }
  }
`;

function indicatorHasAttributes(indicator: Indicator) {
  return indicator.layers.some((layer) => layer.attributes.length > 0);
}
